/**
 * 页面运行时多语言翻译核心工具
 * - 扫描 DOM 文本节点 + 4 个可翻译属性 (placeholder/title/aria-label/alt)
 * - 过滤纯数字、URL、邮箱、日期等无需翻译内容
 * - 双层缓存：L0 内存 Map + L1 localStorage（按 "lang" 分区，TTL 7 天）
 * - 静态字典优先，未命中再调用混元翻译 API
 * - MutationObserver 监听 DOM 变化，做增量翻译
 * - 滑动窗口并发：同时最多 5 个 in-flight 请求
 * - 翻译进行中互斥（禁止并发切换语言/重新翻译）
 * - 阿拉伯语等自动切换 dir=rtl
 * - 用户选择写入 localStorage，刷新/重进保持
 *
 * 用法：
 *   import { pageTranslator, LANG_LIST, getStoredLang, applyDir } from '@/utils/pageTranslator';
 *   pageTranslator.init({ root: document.body });
 *   pageTranslator.switchTo('ar-SA');
 */

import store from '@/store';
import { ruleAPI } from '@/http/fetch';

// 默认源语言
export const SOURCE_LANG = 'zh-CN';

/* -------------------------- 翻译 API（混元 AITranslation） -------------------------- */

// 移动端翻译模型配置 key（七彩石）
// 值格式：{ "name": "hunyuan-mt-7b", "key": "473288e2-...", "429key": "ae0d3499-..." }
const CFG_TRANSLATE_MODEL = 'multi_language_key_mobile';

// 兜底默认值（配置未加载时使用）
const DEFAULT_MODEL_NAME = 'hunyuan-mt-7b';
const DEFAULT_API_KEY = '8d935b2d-8c08-46cc-b4e2-06ab9f244fba';

// 单次翻译 API 超时（ms）
// 混元 7B 偶发长尾响应（>10s），必须超时熔断，避免：
//   1) 滑窗 in-flight 卡住，整个 _translateMissing 永远不释放
//   2) translating 锁迟迟不释放，用户切不了语言、UI 显示"翻译中..."
const TRANSLATE_API_TIMEOUT = 8000;

// 前端语言标识 → 混元翻译 API 的语言代码
const HUNYUAN_LANG_CODE_MAP = {
  'zh-CN': 'zh',
  'en-US': 'en',
  'de-DE': 'de',
  'ar-SA': 'ar',
  'ja-JP': 'ja',
  'ko-KR': 'ko',
  'fr-FR': 'fr',
};

// 七彩石配置项：术语表 ID 列表
const CFG_GLOSSARY_IDS = 'multi_language_glossary_ids';

// localStorage key 前缀
const LS_PREFIX = 'pageTranslator:';
const LS_LANG_KEY = `${LS_PREFIX}lang`;
const LS_DICT_PREFIX = `${LS_PREFIX}dict:`; // dict:{targetLang}
const DICT_TTL = 7 * 24 * 60 * 60 * 1000; // 7 天
const MAX_DICT_ENTRIES = 2000;            // 单语言最大缓存条数
const FLUSH_DELAY = 500;                  // L1 异步刷盘延迟（ms）

// 跳过的标签（文本节点扫描用）：这些标签内部的 child text 不参与翻译
//   - TEXTAREA：内部 child text 是用户输入内容
//   - SCRIPT/STYLE/CODE/PRE：代码/样式不翻
//   - IFRAME/CANVAS/SVG/OBJECT/EMBED：非自然语言文本
const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA',
  'IFRAME', 'CANVAS', 'SVG', 'OBJECT', 'EMBED',
]);

// 属性扫描时才要彻底跳过的标签（连元素本身的属性也不处理）：
//   - SCRIPT/STYLE 等本身没有用户可见属性
//   - TEXTAREA/INPUT 必须保留：placeholder/aria-label 等需要翻译
const SKIP_TAGS_FOR_ATTRS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'CANVAS', 'OBJECT', 'EMBED',
]);

// 可翻译属性
const TRANSLATABLE_ATTRS = ['placeholder', 'title', 'aria-label', 'alt'];

// 需要翻译 .value（DOM property）的表单元素标签
// 这些元素的"显示值"通过 input.value/textarea.value/select.value 设置，
// 不会触发 attribute MutationObserver；需要在扫描时主动读取 .value 处理。
// 仅在 readonly/disabled 时翻译（编辑态的值是用户输入数据，不能翻）。
const VALUE_TRANSLATABLE_TAGS = new Set(['INPUT', 'TEXTAREA']);

// 节点上保存 value 原文 / 翻译标记的 key
const ORIG_VALUE_KEY = '__pt_origValue__';
const VALUE_TRANSLATED_FLAG_KEY = '__pt_lastTrValue__';

// 翻译排除选择器：这些容器内部不参与翻译（避免破坏自身/导致循环）
// 全页面监听时，凡是落在这些容器内的 DOM 变化都直接忽略
// 注：LanguageSwitcher 自身根容器已添加 data-no-translate="true"
const EXCLUDE_SELECTORS = [
  '[data-no-translate="true"]',
];

/**
 * 通用"是否值得翻译"判断（不区分目标语言）：
 * 过滤掉 URL / 邮箱 / IP / 纯数字 / 日期 / 时间 / JSON 等无意义内容；
 * 含至少一个中文 / 字母字符的"自然语言文字"才进入候选。
 */
function isMeaningfulText(text) {
  if (!text) return false;
  const value = String(text).trim();
  if (!value) return false;
  // 必须含字母或中文（纯符号/纯数字的不翻）
  if (!/[A-Za-z\u4e00-\u9fa5]/.test(value)) return false;
  // JSON 串
  if ((value.startsWith('{') && value.endsWith('}')) || (value.startsWith('[') && value.endsWith(']'))) {
    try { JSON.parse(value); return false; } catch (e) { /* 继续 */ }
  }
  if (/https?:\/\//i.test(value)) return false;
  if (/\b(?:\d{1,3}\.){3}\d{1,3}\b/.test(value)) return false;
  if (/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/.test(value)) return false;
  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}(\s+\d{1,2}:\d{1,2}(:\d{1,2})?)?$/.test(value)) return false;
  if (/^\d{1,2}:\d{1,2}(:\d{1,2})?$/.test(value)) return false;
  if (/^\d+(\.\d+)?$/.test(value)) return false;
  return true;
}

/**
 * 判断文本是否"已经是目标语言"，从而无需翻译。
 *
 * 设计思路（修复 BUG 2 后）：
 *   源语言永远是中文。目标语言一旦不含 CJK，再去精细区分 en / de / fr 几乎不可能
 *   （"Speichern" 没有 äöü，但确实是德语；"Submit" 在所有拉丁语系语言里都是英文借词）。
 *   过度精细的判定反而会造成"已翻好的德语再次被送翻"的浪费。
 *   因此采用两层兜底：
 *     1) 含中文（源语言）→ 必须翻译，直接返回 false
 *     2) 文本里完全没有自然语言字母 → 当作"无可翻译内容"，返回 true（跳过）
 *     3) 目标语言是 zh-CN：含中文即"已是中文"
 *     4) 目标语言是 ar-SA：含阿语字母即"已是阿语"
 *     5) 其他拉丁语系（en-US / de-DE / fr-FR ...）：只要不含中文且主要由拉丁字符组成
 *        就视为"已是目标语言"，跳过翻译。这样可能会让"刚切到德语的英文残留"漏翻一次，
 *        但配合 cache + 节点级 TRANSLATED_FLAG_KEY，整体开销远低于反复全量请求。
 */
const LATIN_LANG_SET = new Set(['en-US', 'de-DE', 'fr-FR']);

function isAlreadyInLang(text, targetLang) {
  const value = String(text).trim();
  if (!value) return true;
  // 含中文（源语言）→ 一定要翻
  const hasCN = /[\u4e00-\u9fa5]/.test(value);
  if (hasCN) return false;

  // 不含任何自然语言字母 → 不属于自然语言，不翻
  if (!/[A-Za-z\u00C0-\u024F\u0600-\u06FF]/.test(value)) return true;

  switch (targetLang) {
    case 'zh-CN':
      return hasCN;
    case 'ar-SA':
      // 切阿语时：含阿语字母 → 已是阿语
      return /[\u0600-\u06FF]/.test(value);
    default:
      // 拉丁语系（en/de/fr 等）：不含中文 + 不含阿语字母 + 含拉丁字母即视为已是目标语言
      // 注意：这里有意"宽松判定"，避免已翻好的德语（如 "Speichern"）被反复送翻。
      if (LATIN_LANG_SET.has(targetLang)) {
        return !/[\u0600-\u06FF]/.test(value);
      }
      // 其他未声明的语言保持保守：不跳过
      return false;
  }
}

/**
 * 判断文本是否需要翻译（结合目标语言）
 */
function shouldTranslate(text, targetLang) {
  if (!isMeaningfulText(text)) return false;
  if (targetLang && isAlreadyInLang(text, targetLang)) return false;
  return true;
}

/* -------------------------- 缓存（双层 + 容量上限 + idle 刷盘） -------------------------- */

class TranslationCache {
  constructor() {
    /**
     * @type {Map<string, { entries: Map<string, {text:string, ts:number}>, dirty: boolean, flushTimer: any }>}
     * lang -> 分区
     */
    this.partitions = new Map();
  }

  _getPartition(lang) {
    let p = this.partitions.get(lang);
    if (p) return p;

    // 首次访问从 LS 加载
    const entries = new Map();
    try {
      const raw = localStorage.getItem(LS_DICT_PREFIX + lang);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && obj.entries) {
          const now = Date.now();
          Object.keys(obj.entries).forEach((src) => {
            const item = obj.entries[src];
            if (!item) return;
            // 兼容旧格式 { text, ts } / 字符串
            if (typeof item === 'string') {
              entries.set(src, { text: item, ts: now });
            } else if (item.text && (now - (item.ts || 0) <= DICT_TTL)) {
              entries.set(src, item);
            }
          });
        }
      }
    } catch (e) {
      console.warn('[pageTranslator] 读取持久化字典失败:', e);
    }
    p = { entries, dirty: false, flushTimer: null };
    this.partitions.set(lang, p);
    return p;
  }

  /**
   * 加载并注入静态词典（multi_language_dict_xxx 配置项）
   * 静态词典优先级最高，覆盖任何已有缓存项
   */
  load(lang, staticDict) {
    const p = this._getPartition(lang);
    if (staticDict && typeof staticDict === 'object') {
      const now = Date.now();
      Object.keys(staticDict).forEach((k) => {
        const v = staticDict[k];
        if (typeof v === 'string' && v) {
          p.entries.set(k, { text: v, ts: now });
        }
      });
      // 静态词典也算"脏"，但只是为了下次启动时直接命中；持久化到 LS 没问题
      p.dirty = true;
      this._scheduleFlush(lang, p);
    }
  }

  get(lang, src) {
    const p = this.partitions.get(lang);
    if (!p) return undefined;
    const item = p.entries.get(src);
    if (!item) return undefined;
    // 过期检查
    if (Date.now() - (item.ts || 0) > DICT_TTL) {
      p.entries.delete(src);
      p.dirty = true;
      this._scheduleFlush(lang, p);
      return undefined;
    }
    return item.text;
  }

  /**
   * 批量写入；超出 MAX_DICT_ENTRIES 时按时间淘汰最旧的一半
   */
  set(lang, pairs) {
    const p = this._getPartition(lang);
    const now = Date.now();
    const map = pairs instanceof Map ? pairs : new Map(Object.entries(pairs || {}));
    map.forEach((v, k) => {
      if (typeof v === 'string' && v) p.entries.set(k, { text: v, ts: now });
    });
    if (p.entries.size > MAX_DICT_ENTRIES) {
      this._evictOldestHalf(p);
    }
    p.dirty = true;
    this._scheduleFlush(lang, p);
  }

  _evictOldestHalf(p) {
    const arr = [...p.entries.entries()];
    arr.sort((a, b) => (a[1].ts || 0) - (b[1].ts || 0));
    const keep = Math.ceil(arr.length / 2);
    p.entries = new Map(arr.slice(arr.length - keep));
  }

  _scheduleFlush(lang, p) {
    if (p.flushTimer) return;
    const run = () => {
      p.flushTimer = null;
      if (!p.dirty) return;
      p.dirty = false;
      this._writeLs(lang, p);
    };
    if (typeof requestIdleCallback === 'function') {
      p.flushTimer = requestIdleCallback(run, { timeout: FLUSH_DELAY });
    } else {
      p.flushTimer = setTimeout(run, FLUSH_DELAY);
    }
  }

  _writeLs(lang, p) {
    try {
      const obj = { entries: {}, updatedAt: Date.now() };
      p.entries.forEach((v, k) => { obj.entries[k] = v; });
      localStorage.setItem(LS_DICT_PREFIX + lang, JSON.stringify(obj));
    } catch (e) {
      // 配额不足：清理最旧一半再写一次
      console.warn('[pageTranslator] 写入持久化字典失败，尝试清理:', e);
      try {
        this._evictOldestHalf(p);
        const obj = { entries: {}, updatedAt: Date.now() };
        p.entries.forEach((v, k) => { obj.entries[k] = v; });
        localStorage.setItem(LS_DICT_PREFIX + lang, JSON.stringify(obj));
      } catch (e2) {
        console.warn('[pageTranslator] 清理后仍写入失败:', e2);
      }
    }
  }

  /** 强制把所有脏分区刷盘（用于 beforeunload） */
  flushAll() {
    this.partitions.forEach((p, lang) => {
      if (p.dirty) {
        p.dirty = false;
        this._writeLs(lang, p);
      }
    });
  }

  clear(lang) {
    if (lang) {
      this.partitions.delete(lang);
      try { localStorage.removeItem(LS_DICT_PREFIX + lang); } catch (e) {}
    } else {
      // 清空所有语言的 localStorage 缓存
      this.partitions.forEach((_, key) => {
        try { localStorage.removeItem(LS_DICT_PREFIX + key); } catch (e) {}
      });
      // 兜底：扫描 localStorage 中所有以 LS_DICT_PREFIX 开头的 key 一并清除
      // （防止 partitions 里漏记的旧分区残留）
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith(LS_DICT_PREFIX)) keysToRemove.push(k);
        }
        keysToRemove.forEach((k) => { try { localStorage.removeItem(k); } catch (e) {} });
      } catch (e) { /* ignore */ }
      this.partitions.clear();
    }
  }
}

// beforeunload 强制刷盘，避免脏数据丢失
let _globalCacheRef = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (_globalCacheRef) {
      try { _globalCacheRef.flushAll(); } catch (e) {}
    }
  });
}

/* -------------------------- 翻译 API（混元） -------------------------- */

// 单条请求最大字符数（含编号、分隔符等所有内容）
const MAX_CHARS_PER_REQUEST = 500;
// 多条文本之间的分隔符：使用双换行，与自然语言段落习惯一致，
// 模型不容易"吃掉"分隔（相比特殊标记 <|SEP|> 鲁棒性更好）
const SEP = '\n\n';
// 单条文本前置编号格式：[1]、[2] ...（1-based，与 PC 端一致）
const numTag = (i) => `[${i + 1}]`;

// 不可见前缀：BOM、LRM/RLM、LRE/RLE/PDF、LRI/RLI/PDI、零宽空格 等（RTL 语言常见）
const INVISIBLE_PREFIX_REGEX = /^[\s\uFEFF\u200B-\u200F\u202A-\u202E\u2066-\u2069]+/;

/**
 * 阿拉伯-印度数字 / 扩展数字 / 全角数字 → ASCII 0-9
 * 模型把编号 [1] 翻成 [١] 时，避免 parseInt 失败
 */
function normalizeDigits(s) {
  if (!s) return s;
  return s.replace(/[\u0660-\u0669\u06F0-\u06F9\uFF10-\uFF19]/g, (ch) => {
    const code = ch.charCodeAt(0);
    if (code >= 0x0660 && code <= 0x0669) return String(code - 0x0660);
    if (code >= 0x06F0 && code <= 0x06F9) return String(code - 0x06F0);
    if (code >= 0xFF10 && code <= 0xFF19) return String(code - 0xFF10);
    return ch;
  });
}

/**
 * 修正模型常见错误：
 *   - 半转义：\u003c → <、\u003e → >
 *   - 编号 ] 错成 >：行/段开头处的 [3> → [3]
 */
function normalizeModelOutput(text) {
  return (text || '')
    .replace(/\\u003c/gi, '<')
    .replace(/\\u003e/gi, '>')
    .replace(/\u003c/gi, '<')
    .replace(/\u003e/gi, '>')
    .replace(/(^|[\n\r\s>])\[(\d+)>/g, '$1[$2]');
}

/**
 * 把待翻译数组按字符上限打包（含去重）：
 *   - 同一文本仅打包一次（首次出现的下标作为编号）
 *   - 每个 chunk 最大 MAX_CHARS_PER_REQUEST 字符（含编号 + 分隔符）
 *   - 单条原文超长时，独立成 chunk
 *
 * @param {string[]} texts
 * @returns {Array<{indices:number[], texts:string[], packed:string}>}
 */
function packTexts(texts) {
  const chunks = [];
  let cur = { indices: [], texts: [], packed: '' };
  const seen = new Set();
  const sepLen = SEP.length;

  const flush = () => {
    if (cur.indices.length) chunks.push(cur);
    cur = { indices: [], texts: [], packed: '' };
  };

  for (let i = 0; i < texts.length; i++) {
    const t = texts[i];
    if (typeof t !== 'string' || !t) continue;
    if (seen.has(t)) continue; // 去重
    seen.add(t);

    const tag = numTag(i);
    const itemStr = `${tag}${t}`; // 编号紧跟原文，省字符
    const appendCost = cur.indices.length ? itemStr.length + sepLen : itemStr.length;

    if (cur.indices.length && cur.packed.length + appendCost > MAX_CHARS_PER_REQUEST) {
      flush();
    }

    if (cur.packed) cur.packed += SEP + itemStr;
    else cur.packed = itemStr;
    cur.indices.push(i);
    cur.texts.push(t);

    if (cur.packed.length >= MAX_CHARS_PER_REQUEST) flush();
  }
  flush();
  return chunks;
}

/**
 * 当 packTexts 切出的分组数 < 期望并发数（默认 5）时，
 * 从"含多条的分组"里把条目拆成更小的分组，目标是让总分组数尽量接近 minConcurrency，
 * 充分利用并发窗口。如果总条目数本身 < minConcurrency，按实际条目数拆。
 *
 * 例如：原本 [{a,b,c,d,e,f}] 一组 → 重打包成 [{a},{b},{c},{d},{e,f}] 五组并发
 *
 * @param {Array<{indices:number[], texts:string[], packed:string}>} chunks
 * @param {number} minConcurrency
 * @returns {Array<{indices:number[], texts:string[], packed:string}>}
 */
function repackForConcurrency(chunks, minConcurrency = 5) {
  if (!chunks || !chunks.length) return chunks;
  // 已经够并发数：直接返回
  if (chunks.length >= minConcurrency) return chunks;

  // 总条目数
  const totalItems = chunks.reduce((acc, c) => acc + (c.indices ? c.indices.length : 0), 0);
  if (totalItems <= 1) return chunks; // 没法再拆

  // 目标分组数 = min(minConcurrency, totalItems)
  const targetGroups = Math.min(minConcurrency, totalItems);
  if (targetGroups <= chunks.length) return chunks;

  // 把所有条目展开重组：尽量平均分配
  /** @type {Array<{i:number, src:string}>} */
  const flatItems = [];
  chunks.forEach((c) => {
    (c.indices || []).forEach((origI, k) => {
      flatItems.push({ i: origI, src: c.texts[k] });
    });
  });

  // 按 round-robin 拆成 targetGroups 个组，再用 SEP+[N] 拼回
  const buckets = Array.from({ length: targetGroups }, () => []);
  flatItems.forEach((it, k) => { buckets[k % targetGroups].push(it); });

  const newChunks = buckets
    .filter((bucket) => bucket.length > 0)
    .map((bucket) => {
      const indices = bucket.map((x) => x.i);
      const texts = bucket.map((x) => x.src);
      const packed = bucket.map((x) => `${numTag(x.i)}${x.src}`).join(SEP);
      return { indices, texts, packed };
    });

  return newChunks;
}

/**
 * 滑动窗口并发执行（与 PC 端 runWithConcurrency 一致语义）：
 * 始终维持 ≤ concurrency 个 in-flight 任务，任一完成立即派发下一个，避免长尾阻塞。
 * @template T
 * @param {Array<T>} items
 * @param {(item: T, index: number) => Promise<any>} runner
 * @param {number} concurrency
 * @returns {Promise<void>}
 */
async function runWithConcurrency(items, runner, concurrency = 5) {
  if (!items || !items.length) return;
  const limit = Math.max(1, concurrency | 0);
  let cursor = 0;
  const inFlight = new Set();

  const launchNext = () => {
    if (cursor >= items.length) return;
    const idx = cursor;
    cursor += 1;
    const p = Promise.resolve()
      .then(() => runner(items[idx], idx))
      .catch((err) => { console.warn('[pageTranslator] runWithConcurrency 任务异常:', err); })
      .finally(() => { inFlight.delete(p); });
    inFlight.add(p);
  };

  while (inFlight.size < limit && cursor < items.length) launchNext();
  while (inFlight.size > 0) {
    // eslint-disable-next-line no-await-in-loop
    await Promise.race(inFlight);
    while (inFlight.size < limit && cursor < items.length) launchNext();
  }
}

/**
 * 解析模型返回（按 [N] 编号回填，PC 同等鲁棒）：
 *
 * 设计要点：
 *   - 不再依赖"分隔符字符串"切分，因为 SEP 改为 \n\n 后，译文内部也可能有 \n\n（模型段落）
 *   - 直接用正则 /(^|\n)[\[【\(（]\s*(\d+)\s*[\]\)】>）]/g 全局扫描编号标记位置，
 *     取相邻两个编号位置之间的文本作为该编号的译文；这对"模型把 SEP 吃掉""模型多输出段落"都鲁棒
 *   - 同编号多次出现时合并（模型重复给同一编号的情况）
 *   - 兼容 [N] / [N> / 【N】 / (N) / （N）等损坏形式
 *   - 不可见 RTL 控制符自动剥除；阿拉伯/全角数字归一化
 *   - 至少 80% 命中视为成功
 *   - 80% 打不到时尝试"行数对齐"按位置兜底（按 \n 切 → 只保留非空行）
 *
 * @param {string} raw
 * @param {Array<{i:number, src:string}>} expectedItems
 * @returns {Object<number,string>} 原下标 -> 译文（仅命中的）
 */
function parseNumberedResponse(raw, expectedItems) {
  const out = {};
  if (!raw || typeof raw !== 'string' || !expectedItems || !expectedItems.length) return out;

  const cleaned = normalizeDigits(normalizeModelOutput(raw));

  const tagToIdx = new Map(); // 1-based tag -> 0-based 原下标
  expectedItems.forEach((it) => { tagToIdx.set(it.i + 1, it.i); });

  // ① 直接用正则扫描编号标记位置（全文），提取 [N]...下一个[M] 之间的文本
  //    匹配位置：行首（\n 之后）或文首；兼容 ( 【 ［ 等变体括号
  const TAG_RE = /(?:^|\n)[\s\uFEFF\u200B-\u200F\u202A-\u202E\u2066-\u2069]*[\[【\(（]\s*(\d+)\s*[\]\)】>）]/g;
  /** @type {Array<{tagNum:number, contentStart:number}>} */
  const markers = [];
  let mm;
  while ((mm = TAG_RE.exec(cleaned)) !== null) {
    const tagNum = parseInt(mm[1], 10);
    if (!Number.isFinite(tagNum)) continue;
    markers.push({ tagNum, contentStart: TAG_RE.lastIndex });
  }

  let matched = 0;
  for (let k = 0; k < markers.length; k++) {
    const { tagNum, contentStart } = markers[k];
    if (!tagToIdx.has(tagNum)) continue;
    const end = k + 1 < markers.length
      ? cleaned.lastIndexOf('\n', markers[k + 1].contentStart) // 回退到下一个标记所在行的起点
      : cleaned.length;
    // end 可能因 lastIndexOf 返回 -1；做保护
    const safeEnd = end > contentStart ? end : cleaned.length;
    let content = cleaned.slice(contentStart, safeEnd);
    // 去首尾空白/RTL 控制符
    content = content.replace(INVISIBLE_PREFIX_REGEX, '').trim();
    if (!content) continue;
    const origIdx = tagToIdx.get(tagNum);
    if (out[origIdx]) {
      out[origIdx] += ' ' + content; // 同编号多次出现：合并
    } else {
      out[origIdx] = content;
      matched++;
    }
  }

  if (matched >= expectedItems.length * 0.8) return out;

  // ② 兜底 A：如果已命中至少 50%，直接返回已命中部分（剩下的靠重试轮补翻）
  //    避免"差一条没命中就整体丢弃 → 前面 N 条已翻但被判失败 → 全部重译"的浪费
  if (matched >= Math.max(1, Math.ceil(expectedItems.length * 0.5))) {
    return out;
  }

  // ③ 兜底 B：行数对齐（只保留非空行后按位置映射）
  const nonEmptyLines = cleaned.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  if (nonEmptyLines.length === expectedItems.length) {
    const fallback = {};
    let fallbackHit = 0;
    expectedItems.forEach((it, k) => {
      let line = nonEmptyLines[k].replace(INVISIBLE_PREFIX_REGEX, '');
      line = line.replace(/^[\[【\(（]\s*\d+\s*[\]\)】>）]\s*/, '').trim();
      if (line) { fallback[it.i] = line; fallbackHit++; }
    });
    if (fallbackHit >= expectedItems.length * 0.5) return fallback;
  }

  // ④ 都不行：返回已命中部分（哪怕只有 1 条），剩下的交给上层重试
  return out;
}

/**
 * 调用混元翻译 API（单次请求）
 * @param {Array<{i:number, src:string}>} expectedItems 本次请求项
 * @param {string} packed 已打包好的请求字符串
 * @param {string} targetLang
 * @returns {Promise<Object<number,string>>}  原下标 -> 译文
 */
/**
 * 调用混元翻译 API（AITranslation 通道，与 PC 端 translateService.js 一致）
 * 直接发送打包好的 [N]文本\n\n[N]文本，模型按编号回译
 *
 * @param {Array<{i:number, src:string}>} expectedItems
 * @param {string} packed 打包好的请求文本（含编号 + 分隔符）
 * @param {string} targetLang  前端语言代码（如 'en-US'）
 * @returns {Promise<Object<number,string>>}  原下标 -> 译文
 */
async function callTranslateApiOnce(expectedItems, packed, targetLang) {
  const targetCode = HUNYUAN_LANG_CODE_MAP[targetLang] || targetLang.slice(0, 2);

  // 模型名称 + API key：从七彩石配置 multi_language_key_mobile 读取
  // 配置值格式：{ "name": "hunyuan-mt-7b", "key": "xxx", "429key": "yyy" }
  let model = DEFAULT_MODEL_NAME;
  let authorization = DEFAULT_API_KEY;
  let fallbackKey = ''; // 429 限频备用 key（仅被限频的请求重试时使用）
  try {
    const modelCfg = store.getters.getConfigItem(CFG_TRANSLATE_MODEL) || '';
    if (modelCfg) {
      const parsed = typeof modelCfg === 'string' ? JSON.parse(modelCfg) : modelCfg;
      if (parsed && typeof parsed === 'object') {
        if (parsed.name && typeof parsed.name === 'string') model = parsed.name;
        if (parsed.key && typeof parsed.key === 'string') authorization = parsed.key;
        if (parsed['429key'] && typeof parsed['429key'] === 'string') fallbackKey = parsed['429key'];
      }
    }
  } catch (e) {
    // 配置解析失败使用兜底默认值
    console.warn('[pageTranslator] 读取翻译模型配置失败，使用默认值:', e);
  }

  // 术语表 ID 列表：从七彩石配置读取，按目标语言取对应的 ids 数组
  // 配置值格式：{ "en-US": ["id1"], "de-DE": ["id2"], "ar-SA": ["id3"] }
  // 最大支持 10 个术语库（服务端限制）
  let glossaryIds = [];
  try {
    const glossaryCfg = store.getters.getConfigItem(CFG_GLOSSARY_IDS) || '';
    if (glossaryCfg) {
      const parsed = typeof glossaryCfg === 'string' ? JSON.parse(glossaryCfg) : glossaryCfg;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        // 按目标语言取对应的 ids 数组
        const langIds = parsed[targetLang] || parsed[targetCode] || [];
        if (Array.isArray(langIds)) {
          glossaryIds = langIds.filter((id) => typeof id === 'string' && id.trim()).slice(0, 10);
        }
      }
    }
  } catch (e) {
    // 配置解析失败不影响翻译，降级为不使用术语库
    console.warn('[pageTranslator] 读取术语表配置失败:', e);
  }

  // 超时熔断：用 Promise.race 包裹整个请求 + json 解析
  // 不直接用 AbortController 是因为 ruleAPI 内部封装未必透传 signal，
  // 这里用 race 保证至少 8s 后回到调用方，让上层放弃本 chunk 进入下一轮重试。
  let timer = null;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const err = new Error(`翻译 API 超时（>${TRANSLATE_API_TIMEOUT}ms）`);
      err.code = 'TRANSLATE_API_TIMEOUT';
      reject(err);
    }, TRANSLATE_API_TIMEOUT);
  });

  // 构造翻译请求 Data 字段
  const requestData = {
    model,
    text: packed,
    target: targetCode,
    source: 'zh',
    stream: false,
  };
  // 有术语表 ID 时追加 glossary_ids 字段
  if (glossaryIds.length) {
    requestData.glossary_ids = glossaryIds;
  }

  const requestPromise = (async () => {
    const response = await ruleAPI('e2e', {
      Action: 'NetOpsAI',
      Method: 'GlossaryProxy',
      SystemId: '2',
      Authorization: authorization,
      path: '/openapi/v1/translations',
      Data: requestData,
    });
    return response.json();
  })();

  let result;
  try {
    result = await Promise.race([requestPromise, timeoutPromise]);
  } finally {
    if (timer) clearTimeout(timer);
  }


  // 检测 429 限频：仅对当次被限频的请求用备用 key 重试
  if (result && result.error && String(result.error.type) === '429') {
    if (fallbackKey) {
      console.warn('[pageTranslator] 检测到 429 限频，当次请求用备用 key 重试');
      const retryResponse = await ruleAPI('e2e', {
        Action: 'NetOpsAI',
        Method: 'GlossaryProxy',
        SystemId: '2',
        Authorization: fallbackKey,
        path: '/openapi/v1/translations',
        Data: requestData,
      });
      result = await retryResponse.json();
      // 重试后仍然 429 → 直接抛错
      if (result && result.error && String(result.error.type) === '429') {
        throw new Error('翻译 API 限频（备用 key 也被限频）');
      }
    } else {
      throw new Error('翻译 API 限频（429），且无备用 key');
    }
  }

  // 兼容多种返回格式（与 PC 端 callHunyuanTranslate 一致）
  const translated = (result && result.data && result.data.choices && result.data.choices[0] && result.data.choices[0].message && result.data.choices[0].message.content)
    || (result && result.Data && result.Data.choices && result.Data.choices[0] && result.Data.choices[0].message && result.Data.choices[0].message.content)
    || (result && result.choices && result.choices[0] && result.choices[0].message && result.choices[0].message.content)
    || (result && result.data && result.data.text)
    || (result && result.Data && result.Data.text)
    || null;

  if (!translated) throw new Error('翻译 API 返回为空');

  return parseNumberedResponse(translated, expectedItems);
}

/* -------------------------- 滑动窗口并发 -------------------------- */

class SlidingWindow {
  constructor(limit = 5) {
    this.limit = limit;
    this.queue = [];
    this.running = 0;
  }

  push(taskFn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ taskFn, resolve, reject });
      this._dispatch();
    });
  }

  _dispatch() {
    while (this.running < this.limit && this.queue.length) {
      const { taskFn, resolve, reject } = this.queue.shift();
      this.running += 1;
      Promise.resolve()
        .then(() => taskFn())
        .then((v) => { this.running -= 1; resolve(v); this._dispatch(); })
        .catch((e) => { this.running -= 1; reject(e); this._dispatch(); });
    }
  }
}

/* -------------------------- DOM 扫描与回写 -------------------------- */

const ORIG_TEXT_KEY = '__pt_origText__';
const ORIG_ATTR_PREFIX = '__pt_orig_';
// 已翻译标记：保存"该节点最近一次翻译的 (语言, 原文 normalized key)"
//   - 切语言 / 切原文时不命中，会重新翻译
//   - 同语言、同原文、同节点 → 直接跳过（避免反复发请求）
const TRANSLATED_FLAG_KEY = '__pt_lastTranslated__';
const ATTR_TRANSLATED_FLAG_PREFIX = '__pt_lastTrAttr_';
// 高频变化节点标记：被 observer 检测为"高频抖动"后置为 true，
//   - scanRoot 扫描时直接跳过，不参与翻译
//   - 用于兜底"用户自定义待办里有秒级变化的实时数据"等场景
const HIGH_FREQ_FLAG_KEY = '__pt_highFreq__';
// 高频判定阈值：1 秒内同一节点 nodeValue 变化次数 >= MAX_CHANGES → 标记
const HIGH_FREQ_WINDOW_MS = 1000;
const HIGH_FREQ_MAX_CHANGES = 3;

/**
 * 收集 root 下所有需翻译的文本节点 / 属性
 * @returns {{ items: Array<{src:string, hooks:Array<Function>}> , srcs: string[] }}
 *  items: 反向索引 src -> 多个回写钩子
 */
/** 规范化：合并多空白 + 去首尾，作为缓存 key（与 PC 一致） */
function normalizeKey(text) {
  if (!text) return '';
  return String(text).replace(/\s+/g, ' ').trim();
}

function scanRoot(root, targetLang) {
  const map = new Map(); // normalizedKey -> { src, hooks }

  function pushHook(rawSrc, hook) {
    if (!shouldTranslate(rawSrc, targetLang)) return;
    const key = normalizeKey(rawSrc);
    if (!key) return;
    if (!map.has(key)) map.set(key, { src: key, hooks: [] });
    map.get(key).hooks.push(hook);
  }

  // 1) 文本节点：保留首尾空白回写
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentNode;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (SKIP_TAGS.has(parent.nodeName)) return NodeFilter.FILTER_REJECT;
      if (parent.closest && parent.closest('[data-no-translate="true"]')) return NodeFilter.FILTER_REJECT;
      // 高频抖动节点（如倒计时、实时数据）：observer 已标记，扫描时直接跳过
      if (node[HIGH_FREQ_FLAG_KEY]) return NodeFilter.FILTER_REJECT;
      if (!node.nodeValue || !shouldTranslate(node.nodeValue, targetLang)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  while ((n = walker.nextNode())) {
    const currentVal = n.nodeValue;
    // 缓存原文（用于切回）
    // 注：节点的 nodeValue 在用户操作下可能动态变化（如 Picker 选项切换：正常→异常）。
    //   - 若 currentVal 与已记录的 ORIG_TEXT_KEY 不同，且 currentVal 含中文（即非译文），
    //     说明 Vue 把原文换成了新原文，需要重置 ORIG_TEXT_KEY 与翻译标记。
    //   - 若 currentVal 是英文/已翻译的译文，则 ORIG_TEXT_KEY 保持不变（避免把译文当原文）。
    if (n[ORIG_TEXT_KEY] === undefined) {
      n[ORIG_TEXT_KEY] = currentVal;
    } else if (currentVal !== n[ORIG_TEXT_KEY]) {
      // currentVal 是原文（含中文）才更新；否则当作译文，保留旧 ORIG
      const looksLikeNewSrc = isMeaningfulText(currentVal)
        && /[\u4e00-\u9fa5]/.test(currentVal);
      if (looksLikeNewSrc) {
        n[ORIG_TEXT_KEY] = currentVal;
        // 原文变了，旧的翻译标记失效
        if (n[TRANSLATED_FLAG_KEY] !== undefined) delete n[TRANSLATED_FLAG_KEY];
      }
    }
    const rawSrc = n[ORIG_TEXT_KEY];
    const key = normalizeKey(rawSrc);

    // ★ 已翻译标记 + 当前 nodeValue 真的不是原文 才跳过
    //   修复：某些 Vue 重渲染场景下会把 nodeValue 改回原文（中文）但又不更新 ORIG，
    //   仅看 flag 会导致跳过 → 永远不再翻译。
    //   → 必须额外校验：trim 后的 currentVal 真的不等于 ORIG（即确实是译文）。
    if (
      n[TRANSLATED_FLAG_KEY] === `${targetLang}|${key}`
      && String(currentVal).trim() !== String(rawSrc).trim()
    ) continue;

    // 闭包捕获节点；hook 内动态读取首尾空白以兼容动态变化
    const node = n;
    const flagValue = `${targetLang}|${key}`;
    pushHook(rawSrc, (translated) => {
      const original = node.nodeValue || '';
      const leading = (original.match(/^(\s*)/) || ['', ''])[1];
      const trailing = (original.match(/(\s*)$/) || ['', ''])[1];
      node.nodeValue = leading + translated + trailing;
      // ★ 写入后打标记：本节点本语言本原文已翻译过
      node[TRANSLATED_FLAG_KEY] = flagValue;
    });
  }

  // 2) 可翻译属性（属性无首尾空白概念，整体写）
  //    注意：TEXTAREA/INPUT 等表单控件本身的 placeholder/aria-label 必须翻译，
  //    所以这里用 SKIP_TAGS_FOR_ATTRS（比文本扫描的 SKIP_TAGS 范围更窄）
  const elWalker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
    acceptNode(el) {
      if (SKIP_TAGS_FOR_ATTRS.has(el.nodeName)) return NodeFilter.FILTER_REJECT;
      if (el.getAttribute && el.getAttribute('data-no-translate') === 'true') return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let el;
  while ((el = elWalker.nextNode())) {
    TRANSLATABLE_ATTRS.forEach((attr) => {
      if (!el.hasAttribute(attr)) return;
      const origKey = ORIG_ATTR_PREFIX + attr;
      const currentAttrVal = el.getAttribute(attr);
      // 与文本节点同理：检查属性值是否被用户操作改成了新原文
      if (!el[origKey]) {
        el[origKey] = currentAttrVal;
      } else if (currentAttrVal !== el[origKey]) {
        const looksLikeNewSrc = isMeaningfulText(currentAttrVal)
          && /[\u4e00-\u9fa5]/.test(currentAttrVal);
        if (looksLikeNewSrc) {
          el[origKey] = currentAttrVal;
          const attrFlagKeyClear = ATTR_TRANSLATED_FLAG_PREFIX + attr;
          if (el[attrFlagKeyClear] !== undefined) delete el[attrFlagKeyClear];
        }
      }
      const rawSrc = el[origKey];
      if (!shouldTranslate(rawSrc, targetLang)) return;
      const attrKey = normalizeKey(rawSrc);
      const attrFlagKey = ATTR_TRANSLATED_FLAG_PREFIX + attr;
      // ★ 已翻译标记 + 当前属性值真的是"非原文"才跳过
      //   修复：Vue 重渲染（vant 等组件库）会反复 setAttribute 回原文，
      //   此时 currentAttrVal 仍是原文（与 ORIG 相同），不会进入上面的 else 分支重置 flag。
      //   如果只看 flag 就跳过，会导致译文被冲掉后永远不再翻译。
      //   → 必须额外校验：当前属性值确实不等于 ORIG（即真的是译文）才可信。
      if (
        el[attrFlagKey] === `${targetLang}|${attrKey}`
        && currentAttrVal !== el[origKey]
      ) return;

      const elem = el;
      const flagValue = `${targetLang}|${attrKey}`;
      pushHook(rawSrc, (translated) => {
        elem.setAttribute(attr, translated);
        elem[attrFlagKey] = flagValue;
      });
    });

    // 3) 表单控件 .value（DOM property，非 attribute）
    //    vant Field 等通过 domProps.value 设置 input/textarea 的显示值，
    //    这种写法不会触发 MutationObserver 的 attribute 事件，
    //    必须在每次扫描时主动读取 .value 处理。
    //    注意：编辑态（可输入）的 value 是用户数据，不翻；只处理 readonly / disabled。
    if (VALUE_TRANSLATABLE_TAGS.has(el.nodeName)) {
      const isReadonly = el.hasAttribute('readonly') || el.readOnly === true;
      const isDisabled = el.hasAttribute('disabled') || el.disabled === true;
      if (isReadonly || isDisabled) {
        const currentValue = typeof el.value === 'string' ? el.value : '';
        // 缓存原文：与 attribute 同套保护逻辑
        if (el[ORIG_VALUE_KEY] === undefined) {
          el[ORIG_VALUE_KEY] = currentValue;
        } else if (currentValue !== el[ORIG_VALUE_KEY]) {
          const looksLikeNewSrc = isMeaningfulText(currentValue)
            && /[\u4e00-\u9fa5]/.test(currentValue);
          if (looksLikeNewSrc) {
            el[ORIG_VALUE_KEY] = currentValue;
            if (el[VALUE_TRANSLATED_FLAG_KEY] !== undefined) {
              delete el[VALUE_TRANSLATED_FLAG_KEY];
            }
          }
        }
        const rawValueSrc = el[ORIG_VALUE_KEY];
        if (shouldTranslate(rawValueSrc, targetLang)) {
          const valueKey = normalizeKey(rawValueSrc);
          // ★ flag 匹配 + 当前 value 真的是译文（不等于原文）才跳过
          //   防止 Vue 重渲染把 .value 改回原文后永远不再翻
          const flagMatched = el[VALUE_TRANSLATED_FLAG_KEY] === `${targetLang}|${valueKey}`;
          if (!flagMatched || currentValue === el[ORIG_VALUE_KEY]) {
            const elem2 = el;
            const valueFlagValue = `${targetLang}|${valueKey}`;
            pushHook(rawValueSrc, (translated) => {
              try {
                elem2.value = translated;
                // 触发 input 事件让 Vue 等框架感知变化（避免 v-model 把旧值再次写回）
                // 但只在 readonly 时触发，避免污染编辑态数据；这里 isReadonly/isDisabled 已限制
                elem2[VALUE_TRANSLATED_FLAG_KEY] = valueFlagValue;
              } catch (e) { /* ignore */ }
            });
          }
        }
      }
    }
  }

  return {
    items: Array.from(map.values()),
    srcs: Array.from(map.keys()),
  };
}

/**
 * 还原 root 下所有节点为原文
 */
function restoreRoot(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let n;
  while ((n = walker.nextNode())) {
    if (n[ORIG_TEXT_KEY] !== undefined) n.nodeValue = n[ORIG_TEXT_KEY];
    // 清除已翻译标记，下一轮可重新参与翻译
    if (n[TRANSLATED_FLAG_KEY] !== undefined) delete n[TRANSLATED_FLAG_KEY];
  }
  const elWalker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, null);
  let el;
  while ((el = elWalker.nextNode())) {
    TRANSLATABLE_ATTRS.forEach((attr) => {
      const origKey = ORIG_ATTR_PREFIX + attr;
      if (el[origKey]) el.setAttribute(attr, el[origKey]);
      const attrFlagKey = ATTR_TRANSLATED_FLAG_PREFIX + attr;
      if (el[attrFlagKey] !== undefined) delete el[attrFlagKey];
    });
    // 还原表单控件 .value
    if (VALUE_TRANSLATABLE_TAGS.has(el.nodeName) && el[ORIG_VALUE_KEY] !== undefined) {
      try { el.value = el[ORIG_VALUE_KEY]; } catch (e) {}
      if (el[VALUE_TRANSLATED_FLAG_KEY] !== undefined) delete el[VALUE_TRANSLATED_FLAG_KEY];
    }
  }
}

/**
 * 收集"需要被翻译/还原"的所有 DOM 范围：
 *   - 主 root（业务页面根节点）
 *   - body 下其他不在 root 内的直接子元素（portal/弹层/通知容器等）
 *
 * 与 _translateRoot 内 scanScopes 收集逻辑对齐，保证 restore/translate 两端一致。
 *
 * @param {HTMLElement} root
 * @param {(el:Element)=>boolean} [isExcluded] 可选：判断元素是否落在排除区域（如 LanguageSwitcher 自身）
 * @returns {HTMLElement[]}
 */
function collectScanScopes(root, isExcluded) {
  const scopes = [];
  if (root && root.isConnected !== false) scopes.push(root);
  try {
    if (typeof document !== 'undefined' && document.body) {
      Array.from(document.body.children).forEach((el) => {
        if (!el || el.nodeType !== 1) return;
        if (root && root.contains && root.contains(el)) return;
        if (el === root) return;
        if (typeof isExcluded === 'function' && isExcluded(el)) return;
        if (!el.firstChild) return;
        scopes.push(el);
      });
    }
  } catch (e) { /* ignore */ }
  return scopes;
}

/**
 * 还原"主 root + 所有 portal 容器"内的所有节点为原文。
 * 这是 switchTo / retranslate 切换语言时的关键操作：
 * 之前只 restore 主 root 时，body 下的 vant Picker/Popup 等弹窗里残留的旧译文
 * 不会被还原 + 翻译标记不会被清掉，导致切到新语言后弹窗里仍是旧语言。
 *
 * @param {HTMLElement} root
 * @param {(el:Element)=>boolean} [isExcluded]
 */
function restoreAllScopes(root, isExcluded) {
  const scopes = collectScanScopes(root, isExcluded);
  scopes.forEach((sc) => {
    try { restoreRoot(sc); } catch (e) { /* ignore */ }
  });
}

/* -------------------------- 持久化语言选择 -------------------------- */

export function getStoredLang() {
  try {
    return localStorage.getItem(LS_LANG_KEY) || SOURCE_LANG;
  } catch (e) {
    return SOURCE_LANG;
  }
}

export function setStoredLang(lang) {
  try { localStorage.setItem(LS_LANG_KEY, lang); } catch (e) {}
}

/**
 * 判断某语言是否是 RTL 文字方向
 */
export function isRTLLang(lang) {
  const rtlLangs = ['ar-SA', 'ar', 'he', 'fa', 'ur'];
  return rtlLangs.some((l) => lang === l || (typeof lang === 'string' && lang.startsWith(l + '-')));
}

/**
 * 获取当前翻译"模式标识"，用于 Aegis 上报、调试日志、问题定位。
 * 格式：`{modelName}-{maxChars}`，如 `hunyuan-mt-7b-500`
 */
export function getTranslateMode() {
  let modelName = DEFAULT_MODEL_NAME;
  try {
    const cfg = store.getters.getConfigItem(CFG_TRANSLATE_MODEL) || '';
    if (cfg) {
      const parsed = typeof cfg === 'string' ? JSON.parse(cfg) : cfg;
      if (parsed && parsed.name) modelName = parsed.name;
    }
  } catch (e) { /* ignore */ }
  return `${modelName}-${MAX_CHARS_PER_REQUEST}`;
}

/**
 * 应用文本方向（RTL/LTR）。阿拉伯/希伯来等语言切 rtl。
 *
 * 注意：默认作用在传入的 scope 元素上（一般是页面 root，如 .ticket-detail），
 *       不再污染 <html> / <body>。这样切阿语只影响目标页面，不影响整个站点。
 * 同时仍会把 lang 属性写到 <html>，方便 :lang 选择器、a11y 工具识别。
 *
 * @param {string} lang 目标语言（如 'ar-SA' / 'en-US' / 'zh-CN'）
 * @param {HTMLElement} [scope] 作用范围；缺省时不再触碰 DOM 方向属性
 */
export function applyDir(lang, scope) {
  if (typeof document === 'undefined') return;
  const isRTL = isRTLLang(lang);

  // 始终把 lang 写到 <html>（用于 :lang() / a11y），但不再动 <html> 的 dir
  try {
    document.documentElement.setAttribute('lang', lang);
  } catch (e) { /* ignore */ }

  // 仅对 scope 元素设置 dir，作用域局限于当前页面
  if (scope && scope.nodeType === 1) {
    try {
      scope.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    } catch (e) { /* ignore */ }
  }
}

/* -------------------------- 主控制器 -------------------------- */

class PageTranslator {
  constructor() {
    this.cache = new TranslationCache();
    // 让 beforeunload 能拿到 cache 强制刷盘
    _globalCacheRef = this.cache;
    this.window = new SlidingWindow(5);
    this.observer = null;
    this.root = null;
    this.currentLang = SOURCE_LANG;
    this.translating = false; // 互斥锁（仅在"首次翻译"期间生效）
    /**
     * 单调递增的"切换代次"，每次 switchTo 时 +1。
     * 异步流程（_translateMissing / observer 触发的 _translateRoot）持有自己的 epoch，
     * 回写时若与当前 epoch 不一致，说明用户已切到其他语言，旧流程的写入被丢弃。
     * 这就实现了"允许并发切换 + 旧流程优雅作废"的语义。
     */
    this._switchEpoch = 0;
    /** 首次翻译是否已完成（用于决定是否允许并发切换） */
    this._firstTranslateDone = false;
    this.staticDicts = {};    // lang -> dict
    this.options = {};        // lang -> 显示名
    this.maxRetry = 4;        // 缺失补翻最多 4 轮
    /** @type {Map<string, Map<string, Promise<string>>>} 进行中请求：lang -> (src -> Promise<译文>) */
    this.inFlight = new Map();
    /** 引擎自身正在写 DOM 时为 true（屏蔽 MutationObserver 回调，避免循环触发） */
    this._applyingTranslation = false;
  }

  /**
   * 初始化
   * @param {Object} cfg
   * @param {HTMLElement} cfg.root
   * @param {Object<string,string>} [cfg.options]      可选语言列表
   * @param {Object<string,Object<string,string>>} [cfg.staticDicts] 各语言静态词典
   * @param {string} [cfg.initialLang]  初始/恢复的语言（用于 observer 立刻按目标语言工作）
   */
  init(cfg = {}) {
    // 重新 init 时先停掉旧 observer，避免它绑定的是已销毁的旧 root
    // （路由切换时新旧实例可能短暂并存，新 init 必须强制刷新 observer 与 root）
    this._stopObserver();

    this.root = cfg.root || document.body;
    this.options = cfg.options || {};
    this.staticDicts = cfg.staticDicts || {};
    // 引用计数：记录当前活跃的"消费者"数量（如多个 LanguageSwitcher 实例并存时）
    this._refCount = (this._refCount || 0) + 1;

    // 预加载所有语言的字典
    Object.keys(this.options).forEach((lang) => {
      this.cache.load(lang, this.staticDicts[lang] || {});
    });

    // 在启动 observer 前先把 currentLang 设为恢复值，
    // 否则 observer 内部判断 currentLang === SOURCE_LANG 会让早期 mutation 被忽略
    //
    // 修复（BUG 3）：路由切换 / 多实例复用同一个 pageTranslator 时，
    // 必须根据本次 init 的 initialLang 显式重置 currentLang。
    //   - 若传入 initialLang：以传入值为准
    //   - 若未传入：回退到 localStorage 的 lang，仍未取到则回到 SOURCE_LANG
    // 否则旧实例残留的 currentLang（如 en-US）会让新页面在 observer 启动后
    // 自动按旧语言增量翻译，出现"刚进页面就被翻成上次的语言"的异常。
    if (cfg.initialLang) {
      this.currentLang = cfg.initialLang;
    } else {
      this.currentLang = getStoredLang() || SOURCE_LANG;
    }
    // init 完成后通知监听者当前语言（订阅时也会触发一次，这里再 emit 一次确保 init 后的语言被同步）
    this._emitLangChange(this.currentLang);

    // 启动 MutationObserver（增量翻译）
    this._startObserver();
  }

  /**
   * 主入口：切换到目标语言
   *
   * 行为分两个阶段：
   *   1) 首次翻译期（_firstTranslateDone === false）：
   *      互斥锁保护，并发切换抛 TRANSLATION_BUSY；
   *      防止首屏初始化阶段出现"未完成的旧 epoch + 新 epoch"叠加。
   *
   *   2) 首次翻译完成之后：
   *      允许用户随时切换，不再阻塞；
   *      用 _switchEpoch 单调递增标记本次"切换代次"，旧流程的所有异步写入
   *      （_applyHooks / observer 增量翻译）都会因 epoch 不匹配自动作废。
   *      用户体验上是即点即切，旧请求即便回包也不会污染新 DOM。
   *
   * @throws {Error} 'TRANSLATION_BUSY' 首次翻译进行中
   * @throws {Error} 'NOT_INITIALIZED' 未初始化
   */
  async switchTo(targetLang) {
    if (!this.root) {
      const err = new Error('翻译器未初始化');
      err.code = 'NOT_INITIALIZED';
      throw err;
    }
    // 仅在"首次翻译"阶段强制互斥；后续允许并发切换
    if (this.translating && !this._firstTranslateDone) {
      const err = new Error('翻译进行中，请稍候再试');
      err.code = 'TRANSLATION_BUSY';
      throw err;
    }

    // 切换代次 +1：所有旧 epoch 的异步流程从此刻起被视为"过期"
    const epoch = ++this._switchEpoch;

    this._setBusy(true);
    try {
      // 切回中文：还原原文
      if (targetLang === SOURCE_LANG) {
        this._stopObserver(); // 还原期间不要监听变更
        // 清空 in-flight 占位：旧 epoch 的请求若仍在飞行，回包时会被 epoch 校验拦截
        if (this.inFlight && this.inFlight.size) this.inFlight.clear();
        // 取消 observer 节流定时器，避免旧 mutation 收集延迟触发到新语言
        if (this._patchTimer) { clearTimeout(this._patchTimer); this._patchTimer = null; }
        restoreAllScopes(this.root, (el) => this._isExcluded(el));
        // 还原 document.title
        this._restoreDocTitle();
        this.currentLang = SOURCE_LANG;
        setStoredLang(SOURCE_LANG);
        applyDir(SOURCE_LANG, this.root);
        this._emitLangChange(SOURCE_LANG);
        this._startObserver();
        return;
      }

      // 切到其他语言：先把 DOM 还原成原文，再翻译
      const prevLang = this.currentLang;
      if (prevLang && prevLang !== SOURCE_LANG && prevLang !== targetLang) {
        this._stopObserver();
        restoreAllScopes(this.root, (el) => this._isExcluded(el));
        this._startObserver();
      }

      // 清空残留的 in-flight 占位 + observer 节流（避免旧 mutation 触发到新语言）
      if (this.inFlight && this.inFlight.size) this.inFlight.clear();
      if (this._patchTimer) { clearTimeout(this._patchTimer); this._patchTimer = null; }

      this.currentLang = targetLang;
      setStoredLang(targetLang);
      applyDir(targetLang, this.root);
      this._emitLangChange(targetLang);

      // 确保静态字典加载
      this.cache.load(targetLang, this.staticDicts[targetLang] || {});

      // 翻译执行：传入本次 epoch，内部异步写入会校验 epoch 是否仍然"当前"
      await this._translateRoot(this.root, targetLang, epoch);

      // 翻译 document.title（不在 body 内，需专门处理）
      this._translateDocTitle(targetLang);

      // 补扫：切语言完成后 Vue 可能还在异步渲染（Picker 内部项、响应式回写等），
      // 200ms 后再扫一次。补扫流程同样校验 epoch。
      setTimeout(() => {
        // 用户已切到别的语言（epoch 不再是当前）→ 取消补扫
        if (epoch !== this._switchEpoch) return;
        if (this.currentLang === targetLang && this.root) {
          this._translateRoot(this.root, targetLang, epoch).catch(() => {});
        }
      }, 200);

      // 标记"首次翻译已完成"：之后允许并发切换
      this._firstTranslateDone = true;
    } finally {
      this._setBusy(false);
    }
  }

  /**
   * 当前是否处于翻译进行中（语言切换 / 增量补翻 / 恢复轮询）
   * 任一种繁忙状态都会返回 true。
   */
  isBusy() {
    return !!this.translating || (this._bgBusyCount || 0) > 0;
  }

  /**
   * 是否已完成"首次翻译"。
   * UI 据此决定是否在切换时显示 loading：
   *   - 首次翻译前：busy 期间禁用按钮（防止首屏 init 还在跑就切语言导致不一致）
   *   - 首次翻译后：始终允许切换，旧流程靠 epoch 校验作废，UI 上不展示 loading
   */
  isFirstTranslateDone() {
    return !!this._firstTranslateDone;
  }

  /**
   * 订阅翻译繁忙状态变化
   * @param {(busy:boolean)=>void} fn
   * @returns {() => void} 取消订阅
   */
  onBusyChange(fn) {
    if (typeof fn !== 'function') return () => {};
    if (!this._busyListeners) this._busyListeners = new Set();
    this._busyListeners.add(fn);
    return () => { if (this._busyListeners) this._busyListeners.delete(fn); };
  }

  /**
   * 订阅当前语言变化
   * 用于业务组件需要随语言切换调整格式（如 vant 倒计时 format、日期 locale 等）。
   * 回调会立即触发一次（带当前语言），后续切换时再次触发。
   * @param {(lang:string)=>void} fn
   * @returns {() => void} 取消订阅
   */
  onLangChange(fn) {
    if (typeof fn !== 'function') return () => {};
    if (!this._langListeners) this._langListeners = new Set();
    this._langListeners.add(fn);
    // 订阅时立即给一次当前值，方便消费者初始化
    try { fn(this.currentLang); } catch (e) { /* ignore */ }
    return () => { if (this._langListeners) this._langListeners.delete(fn); };
  }

  _emitLangChange(lang) {
    if (!this._langListeners || !this._langListeners.size) return;
    this._langListeners.forEach((fn) => { try { fn(lang); } catch (e) {} });
  }

  _setBusy(val) {
    const v = !!val;
    if (this.translating === v) return;
    this.translating = v;
    if (this._busyListeners && this._busyListeners.size) {
      this._busyListeners.forEach((fn) => { try { fn(v); } catch (e) {} });
    }
  }

  /**
   * 后台任务（增量翻译 / 恢复轮询）的 busy 计数管理。
   * 与 switchTo 的"互斥锁"语义不同：
   *   - switchTo：用户点击切语言，禁止并发，需要互斥
   *   - 后台任务：MutationObserver / 恢复轮询触发，可以多源叠加，
   *     但 UI 需要看到 busy=true（避免"页面还在翻译却显示已完成"）
   * 这里用引用计数 _bgBusyCount 跟踪当前在跑的后台任务数。
   * 任一时刻只要 translating 或 _bgBusyCount > 0，对外的 isBusy() 都为 true。
   */
  _incBgBusy() {
    this._bgBusyCount = (this._bgBusyCount || 0) + 1;
    if (this._bgBusyCount === 1 && !this.translating) {
      // 后台 busy 第一次出现：通知监听器
      if (this._busyListeners && this._busyListeners.size) {
        this._busyListeners.forEach((fn) => { try { fn(true); } catch (e) {} });
      }
    }
  }

  _decBgBusy() {
    if (!this._bgBusyCount) return;
    this._bgBusyCount -= 1;
    if (this._bgBusyCount === 0 && !this.translating) {
      if (this._busyListeners && this._busyListeners.size) {
        this._busyListeners.forEach((fn) => { try { fn(false); } catch (e) {} });
      }
    }
  }

  /**
   * 清空所有翻译缓存（双层：内存 Map + localStorage）。
   * 适用场景：提交待办 / 转派 / 离开页面前，释放缓存空间，避免无限积累。
   * @param {string} [lang] 可选：仅清空指定语言；缺省清空全部
   */
  clearAllCache(lang) {
    try {
      if (lang) {
        this.cache.clear(lang);
      } else {
        this.cache.clear();
      }
    } catch (e) { /* ignore */ }
  }

  /**
   * 对外暴露：手动重新翻译当前根节点
   *
   * 流程（区别于 switchTo 同语言调用）：
   *   1) 还原 DOM 为原文（restoreRoot 会清理所有节点级翻译标记）
   *   2) 清空当前语言的缓存分区（双层缓存，包括 localStorage）
   *   3) 重新执行翻译
   *
   * 场景：模型偶发解析失败 / 漏翻 / 用户对当前译文不满意时，点"重新翻译"按钮
   *
   * @param {Object} [opts]
   * @param {boolean} [opts.clearCache=true] 是否同时清空缓存（默认清，保证真的重新请求）
   */
  async retranslate(opts = {}) {
    if (this.currentLang === SOURCE_LANG) return;
    // 首次翻译尚未完成时仍互斥；之后允许并发（与 switchTo 一致语义）
    if (this.translating && !this._firstTranslateDone) {
      const err = new Error('翻译进行中，请稍候再试');
      err.code = 'TRANSLATION_BUSY';
      throw err;
    }
    if (!this.root) {
      const err = new Error('翻译器未初始化');
      err.code = 'NOT_INITIALIZED';
      throw err;
    }

    // retranslate 也走切换代次：旧异步流程到此作废
    const epoch = ++this._switchEpoch;

    const targetLang = this.currentLang;
    this._setBusy(true);
    try {
      // 1) 还原 DOM 原文 + 清节点翻译标记（覆盖 root + 所有 portal 弹窗）
      this._stopObserver();
      // 清残留 in-flight 与节流定时器
      if (this.inFlight && this.inFlight.size) this.inFlight.clear();
      if (this._patchTimer) { clearTimeout(this._patchTimer); this._patchTimer = null; }
      try { restoreAllScopes(this.root, (el) => this._isExcluded(el)); } catch (e) { /* ignore */ }
      this._startObserver();

      // 2) 清缓存（默认清）：保证下一次真的走 API，避免还拿到旧的错误译文
      const clearCache = opts.clearCache !== false;
      if (clearCache) {
        try { this.cache.clear(targetLang); } catch (e) { /* ignore */ }
        // 重新加载静态字典（clear 后需要重新注入）
        try {
          this.cache.load(targetLang, this.staticDicts[targetLang] || {});
        } catch (e) { /* ignore */ }
      }

      // 3) 重新翻译（带 epoch 校验，用户中途切语言会自动作废本次写入）
      applyDir(targetLang, this.root);
      await this._translateRoot(this.root, targetLang, epoch);
      // 重新翻译 document.title
      this._translateDocTitle(targetLang);
    } finally {
      this._setBusy(false);
    }
  }

  /**
   * 销毁（引用计数：仅当所有消费者都 destroy 时才真正断开 observer）
   * 路由切换时新旧 LanguageSwitcher 实例可能并存：旧实例 beforeDestroy 调用 destroy
   * 时若直接关闭 observer，会误伤刚被新实例 init 启动的 observer。
   */
  destroy() {
    if (typeof this._refCount === 'number' && this._refCount > 1) {
      this._refCount -= 1;
      return; // 还有其他实例在用，不真正断开
    }
    this._refCount = 0;
    this._stopObserver();
    // 清理 root 上残留的 dir，避免离开页面后旧节点 RTL 样式残留
    if (this.root && this.root.nodeType === 1) {
      try { this.root.removeAttribute('dir'); } catch (e) {}
    }
    this.root = null;
  }

  /**
   * 调用 hooks 写 DOM
   * 注：不再用 _applyingTranslation 标志屏蔽 observer，
   *     原因是该标志在异步翻译流程中容易长期为 true，导致用户真实新增内容被忽略；
   *     observer 内部已通过"扫描时仅处理含中文且未翻译过的文本"避免循环触发，
   *     即便 hook 写入触发 mutation，扫描也找不到可翻译项，自然不会重复发请求。
   *
   * 修复（BUG 7）：可选参数 langScope —— 本次 hooks 所属的目标语言。
   *   异步请求回包时，如果 langScope 与当前 this.currentLang 不一致，
   *   说明用户已经切换到了别的语言，旧语言的译文不能再写入 DOM。
   *
   * 增强：可选参数 epoch —— 本次 hooks 所属的"切换代次"。
   *   首次翻译完成后允许并发切换：之前发出的异步流程虽然不再被互斥锁阻塞，
   *   但其回写时若 epoch 不等于 this._switchEpoch，说明用户已切到新语言，
   *   该流程的所有写入都应被丢弃。这是 epoch 的核心作用。
   *
   * 同步路径（缓存命中、首屏初始化）调用方可同时不传 langScope/epoch，行为与之前一致。
   */
  _applyHooks(hooks, value, langScope, epoch) {
    if (!hooks || !hooks.length) return;
    if (langScope && langScope !== this.currentLang) return;
    if (typeof epoch === 'number' && epoch !== this._switchEpoch) return;
    hooks.forEach((h) => { try { h(value); } catch (e) {} });
  }

  /* ---------------- 内部方法 ---------------- */

  /**
   * 翻译"页面所有可见区域"。
   * 不再依赖白名单选择器列举特定组件，直接扫描：
   *   1) 主 root（业务页面根节点）
   *   2) body 下其他不在 root 内的直接子元素（portal/弹层/通知容器等）
   * 这样任何被外置渲染的内容（Vant Picker、Popup、自定义全屏组件等）都会被覆盖。
   *
   * @param {HTMLElement} root 主根节点（默认 .ticket-detail）
   * @param {string} targetLang
   * @param {number} [epoch] 切换代次：用于异步流程作废校验（_applyHooks 写入前会比对）
   */
  async _translateRoot(root, targetLang, epoch) {
    // 入口立即校验：用户可能在调用前已切语言
    if (typeof epoch === 'number' && epoch !== this._switchEpoch) {
      return { scanned: 0, hit: 0, translated: 0 };
    }
    // 收集所有要扫描的范围：主 root + body 下其他独立子节点
    // 与切语言时的 restoreAllScopes 共用 collectScanScopes，确保两端范围一致
    const scanScopes = collectScanScopes(root, (el) => this._isExcluded(el));

    // 合并扫描结果（按 normalized key 去重；不同 scope 的同源文本会合并 hooks）
    const merged = new Map();
    let totalScanned = 0;
    scanScopes.forEach((sc) => {
      const { items } = scanRoot(sc, targetLang);
      totalScanned += items.length;
      items.forEach((it) => {
        if (!merged.has(it.src)) merged.set(it.src, { src: it.src, hooks: [] });
        merged.get(it.src).hooks.push(...it.hooks);
      });
    });
    const items = Array.from(merged.values());

    if (!items.length) {
      return { scanned: 0, hit: 0, translated: 0 };
    }

    // 1) 命中缓存的先回写（带 epoch 校验，避免写入到已被切走的 DOM）
    const missItems = [];
    let hitCount = 0;
    items.forEach((it) => {
      const cached = this.cache.get(targetLang, it.src);
      if (typeof cached === 'string') {
        this._applyHooks(it.hooks, cached, targetLang, epoch);
        hitCount += 1;
      } else {
        missItems.push(it);
      }
    });

    if (missItems.length || hitCount > 0) {
      // eslint-disable-next-line no-console
      console.log(
        `[pageTranslator] _translateRoot 扫描 ${totalScanned}（去重后 ${items.length}），命中 ${hitCount}，待 API ${missItems.length}`,
        {
          targetLang,
          scopes: scanScopes.length,
          sampleAll: items.slice(0, 8).map((x) => x.src),
          sampleMiss: missItems.slice(0, 8).map((x) => x.src),
        }
      );
    }

    if (!missItems.length) {
      return { scanned: items.length, hit: hitCount, translated: 0 };
    }

    // 2) 未命中：分批 + 滑动窗口并发请求（带 epoch 校验）
    await this._translateMissing(missItems, targetLang, epoch);
    return { scanned: items.length, hit: hitCount, translated: missItems.length };
  }

  async _translateMissing(missItems, targetLang, epoch) {
    // ---- 跨调用去重（in-flight 复用）----
    if (!this.inFlight.has(targetLang)) this.inFlight.set(targetLang, new Map());
    const flightMap = this.inFlight.get(targetLang);

    /** @type {Array<{item:any, idx:number}>} 真正需要打包请求的 missItems */
    const toRequest = [];
    /** @type {Array<Promise<void>>} 等待 in-flight 复用的项 */
    const waitTasks = [];

    missItems.forEach((it, idx) => {
      // 已在缓存（双层）中：理论上不会到这里（_translateRoot 已过滤），但兜底再查一次
      const cached = this.cache.get(targetLang, it.src);
      if (typeof cached === 'string') {
        this._applyHooks(it.hooks, cached, targetLang, epoch);
        return;
      }
      // 已有 in-flight：等结果回写当前 hooks
      const pending = flightMap.get(it.src);
      if (pending) {
        waitTasks.push(pending.then((t) => {
          if (typeof t === 'string' && t && t !== it.src) {
            // 异步回写需要带 langScope，防止切换语言后旧译文落到新语言 DOM 上
            this._applyHooks(it.hooks, t, targetLang);
          }
        }).catch(() => {}));
        return;
      }
      // 全新：占位 Promise（resolver 在请求成功时调用）
      let resolver;
      const p = new Promise((resolve) => { resolver = resolve; });
      // 在 Promise 上挂一个 _resolve 方便后续触发
      p._resolve = (val) => { try { resolver(val); } catch (e) {} };
      flightMap.set(it.src, p);
      toRequest.push({ item: it, idx });
    });

    if (!toRequest.length) {
      await Promise.all(waitTasks);
      return;
    }

    // 1) 第一轮：按 ≤500 字符 + [N] 编号 + \n\n 分隔规则打包，
    //    若分组数 < 5，再用 repackForConcurrency 拆成"目标 5 组"，让滑动窗口能跑满 5 路并发
    //    （字段不足 5 时按字段个数全并发）
    const filteredItems = toRequest.map((x) => x.item);
    let pendingItems = filteredItems.slice(); // [{src, hooks}]

    try {
      for (let round = 0; round < this.maxRetry && pendingItems.length; round++) {
        const srcs = pendingItems.map((it) => it.src);
        // 所有轮次统一合并模式：按 ≤500 字符打包 + 均分到 5 组并发
        // （之前第 2 轮起用单条独立请求，容易触发 API 限频）
        let chunks = packTexts(srcs);
        chunks = repackForConcurrency(chunks, this.window.limit || 5);

        // 滑动窗口并发：始终维持 ≤ limit 个 in-flight，任一完成立即补位
        const concurrency = this.window.limit || 5;
        const stillIdxSet = new Set(); // 本轮未命中的 src 索引（在 pendingItems 中的下标）
        await runWithConcurrency(chunks, async (chunk) => {
          const expectedItems = chunk.indices.map((i, k) => ({ i, src: chunk.texts[k] }));
          const map = await this._safeApiOnce(expectedItems, chunk.packed, targetLang);
          const writePairs = {};
          chunk.indices.forEach((origIdx, k) => {
            const t = map[origIdx];
            const src = chunk.texts[k];
            const item = pendingItems[origIdx];
            if (typeof t === 'string' && t.trim()) {
              // 译文 === 原文（人名/英文专有名词等）：也要缓存 + 打标记，避免反复请求
              writePairs[src] = t;
              // 异步回写：带 langScope + epoch 双重校验，避免旧流程的译文写入新 DOM
              if (item) this._applyHooks(item.hooks, t, targetLang, epoch);
            } else {
              stillIdxSet.add(origIdx);
            }
          });
          if (Object.keys(writePairs).length) this.cache.set(targetLang, writePairs);
        }, concurrency);

        // 准备下一轮（仅保留未命中的）
        if (!stillIdxSet.size) { pendingItems = []; break; }
        pendingItems = pendingItems.filter((_, idx) => stillIdxSet.has(idx));
      }
    } finally {
      // 等剩余 in-flight 复用项也跑完
      await Promise.all(waitTasks).catch(() => {});
      // 释放 in-flight 占位（已写入缓存的 src 用译文，未写入的用原文）
      filteredItems.forEach((it) => {
        const p = flightMap.get(it.src);
        if (p) {
          const t = this.cache.get(targetLang, it.src);
          if (typeof p._resolve === 'function') p._resolve(typeof t === 'string' ? t : it.src);
          flightMap.delete(it.src);
        }
      });
    }
  }

  /**
   * 包一层异常保护
   * @returns {Promise<Object<number,string>>}
   */
  async _safeApiOnce(expectedItems, packed, targetLang) {
    try {
      return await callTranslateApiOnce(expectedItems, packed, targetLang);
    } catch (e) {
      console.warn('[pageTranslator] API 异常:', e);
      return {};
    }
  }

  /**
   * 判断节点/元素是否在"翻译排除区域"内（如 LanguageSwitcher 自身）。
   * 命中即不参与翻译、其内部 mutation 也不会触发增量翻译。
   */
  _isExcluded(node) {
    if (!node) return true;
    const el = node.nodeType === 1 ? node : node.parentElement;
    if (!el) return false;
    for (let i = 0; i < EXCLUDE_SELECTORS.length; i++) {
      if (el.closest && el.closest(EXCLUDE_SELECTORS[i])) return true;
    }
    return false;
  }

  /**
   * 翻译 document.title：
   *   document.title 不在 body 子树内，MutationObserver 和 TreeWalker 都扫不到。
   *   需要在切换语言时主动处理：先缓存中文原文，再从翻译缓存/API 获取译文写入。
   *   如果缓存命中直接写；否则把 title 当作一条待翻译文本走 API。
   */
  _translateDocTitle(targetLang) {
    try {
      if (typeof document === 'undefined' || !document.title) return;
      const currentTitle = document.title;
      // 保存原始中文 title（仅第一次）
      if (!this._origDocTitle) this._origDocTitle = currentTitle;
      const src = this._origDocTitle;
      if (!src || !shouldTranslate(src, targetLang)) return;

      // 先查缓存
      const cached = this.cache.get(targetLang, normalizeKey(src));
      if (typeof cached === 'string') {
        document.title = cached;
        return;
      }

      // 没缓存：把 title 加入翻译队列（单独一条请求）
      const packed = `${numTag(0)}${src}`;
      const expectedItems = [{ i: 0, src }];
      callTranslateApiOnce(expectedItems, packed, targetLang)
        .then((map) => {
          const translated = map[0];
          if (typeof translated === 'string' && translated.trim()) {
            // 回写时校验语言是否仍然一致（用户可能已切走）
            if (this.currentLang === targetLang) {
              document.title = translated;
            }
            // 无论如何写入缓存
            this.cache.set(targetLang, { [src]: translated });
          }
        })
        .catch(() => { /* 翻译失败，title 保持不变 */ });
    } catch (e) { /* ignore */ }
  }

  /**
   * 还原 document.title 为原始中文
   */
  _restoreDocTitle() {
    try {
      if (this._origDocTitle && typeof document !== 'undefined') {
        document.title = this._origDocTitle;
      }
    } catch (e) { /* ignore */ }
  }

  /**
   *   同一文本节点 1 秒内 nodeValue 变化 >= HIGH_FREQ_MAX_CHANGES 次 → 标记 HIGH_FREQ_FLAG_KEY，
   *   后续 mutation 直接忽略，scanRoot 也会跳过。
   *   兜底"用户自定义待办里有动态实时数据"等场景，避免每秒翻译的死循环。
   *
   * @param {Node} node 文本节点
   * @returns {boolean} true = 已标记为高频，本次 mutation 应被忽略
   */
  _markHighFreqIfNeeded(node) {
    if (!node) return false;
    // 已标记为高频：直接忽略
    if (node[HIGH_FREQ_FLAG_KEY]) return true;
    // 收集变化时间戳（保留窗口内的）
    let stamps = node.__pt_changeStamps__;
    if (!Array.isArray(stamps)) {
      stamps = [];
      node.__pt_changeStamps__ = stamps;
    }
    const now = Date.now();
    // 清理超过窗口的旧时间戳
    const cutoff = now - HIGH_FREQ_WINDOW_MS;
    while (stamps.length && stamps[0] < cutoff) stamps.shift();
    stamps.push(now);
    if (stamps.length >= HIGH_FREQ_MAX_CHANGES) {
      // 触发标记：永久排除该节点
      node[HIGH_FREQ_FLAG_KEY] = true;
      // 释放时间戳数组占用
      node.__pt_changeStamps__ = null;
      // eslint-disable-next-line no-console
      console.log('[pageTranslator] 检测到高频抖动节点，自动屏蔽:', node.nodeValue && node.nodeValue.slice(0, 30));
      return true;
    }
    return false;
  }

  /* ---------------- 增量翻译（全页面监听，不再用白名单） ---------------- */

  _startObserver() {
    if (typeof MutationObserver === 'undefined') return;
    if (!this.root) return;
    if (this.observer) return; // 已启动

    /**
     * 全局 mutation 处理：
     *   - 直接监听 document.body 全子树，任何 DOM 变化都会被捕获
     *   - 排除"LanguageSwitcher 自身"以及显式标注 data-no-translate 的容器
     *   - 节流合并 200ms：避免高频抖动导致频繁全量扫描
     *   - 真正的"是否重复翻译"由 scanRoot 内的 TRANSLATED_FLAG_KEY 节点级标记控制
     */
    const handleMutations = (mutations) => {
      if (this.currentLang === SOURCE_LANG) return;

      // 至少有一个变更不在排除区域内才需要处理
      let hasMeaningful = false;
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        // 变更目标在排除区域内：忽略（LanguageSwitcher 自身、data-no-translate 标记的容器）
        if (this._isExcluded(m.target)) continue;

        // ★ 高频文本节点自动屏蔽：
        //   有些动态内容（倒计时、实时数据）会以秒级频率改 nodeValue。
        //   如果忘了加 data-no-translate，会触发"每秒翻译"的死循环。
        //   这里做兜底：同一文本节点 1 秒内变化 >= 3 次 → 自动判定为高频，
        //   后续该节点的 mutation 直接忽略，不再触发翻译，并打节点级标记。
        //   注意：仅作用于纯 text node 的 characterData mutation；
        //   childList / attributes 不受影响（避免误伤正常的页面更新）。
        if (m.type === 'characterData' && m.target && m.target.nodeType === 3) {
          if (this._markHighFreqIfNeeded(m.target)) continue;
        }

        if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) {
          hasMeaningful = true; break;
        }
        if (m.type === 'characterData') { hasMeaningful = true; break; }
        if (m.type === 'attributes' && TRANSLATABLE_ATTRS.includes(m.attributeName)) {
          hasMeaningful = true; break;
        }
      }
      if (!hasMeaningful) return;

      // 节流合并：200ms 内合并所有变更后做一次全量扫描
      if (this._patchTimer) clearTimeout(this._patchTimer);
      this._patchTimer = setTimeout(async () => {
        this._patchTimer = null;
        if (!this.root) return;
        const lang = this.currentLang;
        // observer 触发的增量翻译：抓拍当前 epoch，回写时校验
        // 用户切语言后此 epoch 会过期，所有写入将被自动丢弃
        const obsEpoch = this._switchEpoch;
        // 增量翻译参与 busy 计数：让 UI 显示"翻译中"（注意：UI 可能选择不展示，但 isBusy 仍正确反映）
        this._incBgBusy();
        try {
          await this._translateRoot(this.root, lang, obsEpoch).catch(() => {});
        } catch (e) { /* ignore */ } finally {
          this._decBgBusy();
        }
      }, 200);
    };

    // 全局唯一 observer：监听 document.body 整棵子树
    // 这样无论页面在哪个位置（root 内 / root 外 / 新弹层 / 全屏 picker / 自定义 teleport）
    // 出现新的 DOM，都能被捕获，不再需要白名单。
    this.observer = new MutationObserver(handleMutations);
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRS,
    });
  }

  _stopObserver() {
    if (this.observer) {
      try { this.observer.disconnect(); } catch (e) {}
      this.observer = null;
    }
    if (this._patchTimer) {
      clearTimeout(this._patchTimer);
      this._patchTimer = null;
    }
  }
}

export const pageTranslator = new PageTranslator();
